// src/components/ui/Card.tsx — SCE Issues
// Card générique avec bordure gauche optionnelle colorée par métier

import type { CSSProperties, HTMLAttributes, ReactNode } from 'react'
import type { Metier } from '@/types/issue.types'
import { METIERS_COULEURS } from '@/config/metiers.config'

export interface CardProps extends HTMLAttributes<HTMLDivElement> {
  metier?: Metier
  onClick?: () => void
  children: ReactNode
}

/**
 * Card Sartorius.
 * - Toute la carte est zone de touch (large touch target terrain)
 * - Bordure gauche colorée (4px) si `metier` fourni
 * - Ombre douce pour meilleure hiérarchie sur mobile (liberté de design assumée)
 */
export function Card({
  metier,
  onClick,
  children,
  className,
  style,
  ...rest
}: CardProps): JSX.Element {
  const interactive = typeof onClick === 'function'

  const base: CSSProperties = {
    backgroundColor: 'var(--color-bg-card)',
    borderRadius: 12,
    boxShadow: '0 2px 8px rgba(0, 0, 0, 0.06)',
    padding: 'var(--spacing-card-padding)',
    cursor: interactive ? 'pointer' : 'default',
    transition: 'box-shadow 0.15s ease, transform 0.1s ease',
    position: 'relative',
    overflow: 'hidden',
  }

  const metierBorder: CSSProperties = metier
    ? {
        borderLeft: `4px solid ${METIERS_COULEURS[metier]}`,
        paddingLeft: 'calc(var(--spacing-card-padding) - 4px)',
      }
    : {}

  return (
    <div
      {...rest}
      // `card-surface` troque l'ombre portée contre une bordure 1px à partir
      // de 768px (cf. globals.css) : une pile d'ombres est lisible sur mobile,
      // bruyante sur grand écran.
      className={className ? `card-surface ${className}` : 'card-surface'}
      style={{ ...base, ...metierBorder, ...style }}
      onClick={onClick}
      role={interactive ? 'button' : undefined}
      tabIndex={interactive ? 0 : undefined}
      onKeyDown={
        interactive
          ? (e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault()
                onClick?.()
              }
            }
          : undefined
      }
    >
      {children}
    </div>
  )
}
